"use client";

import type { ReactNode } from "react";
import { usePathname } from "next/navigation";
import { supabaseUrl, supabaseAnonKey } from "@/lib/supabase/config";

/**
 * Rute storefront yang BENAR-BENAR butuh sesi Supabase (cermin
 * `PROTECTED_PREFIXES` di `src/middleware.ts`). Hanya rute inilah yang di-gate.
 *
 * Halaman publik (beranda, /cari, /kategori, /login, /admin/login) sengaja TIDAK
 * di-gate: tanpa env mereka tetap me-render shell-nya (degradasi anggun, data
 * kosong) — perilaku lama yang diandalkan smoke test publik & berguna agar
 * storefront tetap terbuka meski mis-konfig.
 */
const GUARDED_PREFIXES = [
  "/orders",
  "/tagihan",
  "/pembayaran",
  "/account",
  "/cart",
  "/poin",
];

/**
 * Gerbang konfigurasi: pastikan kredensial Supabase publik tersedia sebelum
 * me-render halaman yang BERGANTUNG pada sesi Supabase.
 *
 * KENAPA ADA: `NEXT_PUBLIC_SUPABASE_URL` / `NEXT_PUBLIC_SUPABASE_ANON_KEY`
 * di-INLINE saat BUILD (bukan dibaca saat runtime). Bila build dijalankan tanpa
 * kedua nilai ini (mis. setelah migrasi hosting / `next build` tanpa env), maka
 * `createBrowserSupabase()` mengembalikan null → login tak bisa autentikasi,
 * `CustomerContext` tak pernah memuat pembeli, dan middleware (yang juga membaca
 * env yang sama) melewati proteksi auth diam-diam. Akibatnya halaman Keranjang
 * tampil kosong selamanya dan Akun melempar balik ke /login — TANPA petunjuk
 * apa pun soal penyebabnya.
 *
 * Gerbang ini membuat kesalahan konfigurasi itu LANTANG pada rute yang terdampak:
 * alih-alih halaman setengah-rusak yang membingungkan, tampilkan pesan jelas +
 * langkah perbaikan. Saat terkonfigurasi normal (atau di rute publik), komponen
 * ini transparan (render `children` apa adanya).
 */
export function ConfigGuard({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const configured = Boolean(supabaseUrl && supabaseAnonKey);
  const onGuardedRoute = GUARDED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

  if (configured || !onGuardedRoute) {
    return <>{children}</>;
  }

  const missing = [
    !supabaseUrl ? "NEXT_PUBLIC_SUPABASE_URL" : null,
    !supabaseAnonKey ? "NEXT_PUBLIC_SUPABASE_ANON_KEY" : null,
  ].filter(Boolean) as string[];

  return (
    <div className="flex min-h-screen items-center justify-center bg-shell px-5 py-10">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-soft">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">
          Konfigurasi belum lengkap
        </div>
        <h1 className="text-lg font-extrabold text-ink">
          Aplikasi belum terhubung ke Supabase
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          Variabel lingkungan berikut tidak terbaca saat aplikasi di-<em>build</em>,
          sehingga login, keranjang, dan akun tidak bisa berjalan:
        </p>
        <ul className="mt-3 space-y-1.5">
          {missing.map((key) => (
            <li
              key={key}
              className="rounded-lg bg-neutral-100 px-3 py-2 font-mono text-[12px] font-semibold text-ink"
            >
              {key}
            </li>
          ))}
        </ul>
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[12px] leading-relaxed text-amber-800">
          <p className="font-bold">Cara memperbaiki (hosting self-host / Hostinger):</p>
          <ol className="mt-1.5 list-decimal space-y-1 pl-4">
            <li>
              Isi nilai di atas pada <span className="font-mono">.env.local</span>{" "}
              (atau Environment Variables di panel hosting).
            </li>
            <li>
              <span className="font-bold">Build ulang</span> aplikasi (
              <span className="font-mono">npm run build</span>) — nilai{" "}
              <span className="font-mono">NEXT_PUBLIC_*</span> ditanam saat build,
              bukan saat runtime, jadi menyetelnya saja tanpa build ulang tidak cukup.
            </li>
            <li>
              Jalankan ulang server (<span className="font-mono">npm start</span>) /
              deploy ulang.
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
